export type HsViewerProfilerMode = 'off' | 'cpu' | 'deep';

export interface HsViewerProfilerConfig {
  enabled: boolean;
  deepGpu: boolean;
  slowFrameMs?: number;
}

export interface HsViewerProfilerPhaseSnapshot {
  name: string;
  count: number;
  totalMs: number;
  averageMs: number;
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
  lastMs: number;
  slowCount: number;
}

export interface HsViewerProfilerEventSnapshot {
  timestampMs: number;
  name: string;
  detail: string;
}

export interface HsViewerProfilerCounterSnapshot {
  name: string;
  count: number;
  total: number;
  average: number;
  min: number;
  max: number;
  last: number;
}

export interface HsViewerProfilerSnapshot {
  mode: HsViewerProfilerMode;
  phaseCount: number;
  frameCount: number;
  slowFrameCount: number;
  longTaskCount: number;
  longTaskTotalMs: number;
  longTaskMaxMs: number;
  userTimingSupported: boolean;
  longTaskSupported: boolean;
  gpuTimerSupported: boolean;
  gpuTimerMessage: string;
  phases: HsViewerProfilerPhaseSnapshot[];
  counters: HsViewerProfilerCounterSnapshot[];
  events: HsViewerProfilerEventSnapshot[];
}

export interface HsViewerProfilerSpan {
  name: string;
  startedAtMs: number;
  markStart: string | null;
  measureName: string | null;
}

interface ViewerGpuTimerExtension {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
}

export interface HsViewerGpuSpan {
  gl: WebGL2RenderingContext;
  extension: ViewerGpuTimerExtension;
  query: WebGLQuery;
  name: string;
}

type ViewerPendingGpuQuery = HsViewerGpuSpan;

interface ViewerProfilerPhaseData {
  count: number;
  totalMs: number;
  maxMs: number;
  lastMs: number;
  slowCount: number;
  samples: number[];
}

interface ViewerProfilerCounterData {
  count: number;
  total: number;
  min: number;
  max: number;
  last: number;
}

const DEFAULT_SLOW_FRAME_MS: number = 33.3;
const MAX_PHASES: number = 32;
const MAX_SAMPLES_PER_PHASE: number = 120;
const MAX_EVENTS: number = 80;
const MAX_COUNTERS: number = 32;

export class HsViewerProfiler {
  private enabled: boolean = false;
  private deepGpu: boolean = false;
  private slowFrameMs: number = DEFAULT_SLOW_FRAME_MS;
  private sequence: number = 0;
  private readonly phaseData: Map<string, ViewerProfilerPhaseData> = new Map();
  private longTaskObserver: PerformanceObserver | null = null;
  private longTaskCount: number = 0;
  private longTaskTotalMs: number = 0;
  private longTaskMaxMs: number = 0;
  private gpuExtension: ViewerGpuTimerExtension | null = null;
  private gpuTimerMessage: string = 'Deep GPU timing is disabled.';
  private readonly pendingGpuQueries: ViewerPendingGpuQuery[] = [];
  private readonly counterData: Map<string, ViewerProfilerCounterData> = new Map();
  private readonly events: HsViewerProfilerEventSnapshot[] = [];

  public configure(config: HsViewerProfilerConfig, gl: WebGL2RenderingContext | null = null): void {
    const nextEnabled: boolean = config.enabled;
    const nextDeepGpu: boolean = nextEnabled && config.deepGpu;
    const modeChanged: boolean = this.enabled !== nextEnabled || this.deepGpu !== nextDeepGpu;
    this.enabled = nextEnabled;
    this.deepGpu = nextDeepGpu;
    this.slowFrameMs = Number.isFinite(config.slowFrameMs)
      ? Math.max(1, config.slowFrameMs as number)
      : DEFAULT_SLOW_FRAME_MS;

    if (!this.enabled) {
      this.stopLongTaskObserver();
      this.releaseGpuQueries();
      this.gpuExtension = null;
      this.gpuTimerMessage = 'Profiling is disabled.';
      if (modeChanged) {
        this.reset();
      }
      return;
    }

    if (modeChanged) {
      this.reset();
    }
    this.startLongTaskObserver();
    if (this.deepGpu && gl !== null) {
      this.initializeGpuTimer(gl);
    } else if (!this.deepGpu) {
      this.releaseGpuQueries();
      this.gpuExtension = null;
      this.gpuTimerMessage = 'Deep GPU timing is disabled.';
    } else {
      this.gpuTimerMessage = 'Waiting for a WebGL2 context.';
    }
  }

  public destroy(): void {
    this.stopLongTaskObserver();
    this.releaseGpuQueries();
    this.phaseData.clear();
    this.counterData.clear();
    this.events.length = 0;
  }

  public reset(): void {
    this.phaseData.clear();
    this.counterData.clear();
    this.longTaskCount = 0;
    this.longTaskTotalMs = 0;
    this.longTaskMaxMs = 0;
    this.events.length = 0;
  }

  public recordEvent(name: string, detail: string = ''): void {
    if (!this.enabled) {
      return;
    }
    this.events.push({
      timestampMs: performance.now(),
      name,
      detail,
    });
    if (this.events.length > MAX_EVENTS) {
      this.events.shift();
    }
  }

  public startSpan(name: string): HsViewerProfilerSpan | null {
    if (!this.enabled) {
      return null;
    }

    const suffix: string = String(++this.sequence);
    const markStart: string = `hyperscene:${name}:start:${suffix}`;
    const measureName: string = `hyperscene:${name}`;
    const userTimingSupported: boolean =
      typeof performance !== 'undefined' &&
      typeof performance.mark === 'function' &&
      typeof performance.measure === 'function';
    if (userTimingSupported) {
      performance.mark(markStart);
    }
    return {
      name,
      startedAtMs: performance.now(),
      markStart: userTimingSupported ? markStart : null,
      measureName: userTimingSupported ? measureName : null,
    };
  }

  public endSpan(span: HsViewerProfilerSpan | null): void {
    if (span === null) {
      return;
    }

    const durationMs: number = Math.max(0, performance.now() - span.startedAtMs);
    if (span.markStart !== null && span.measureName !== null) {
      const markEnd: string = `${span.markStart}:end`;
      try {
        performance.mark(markEnd);
        performance.measure(span.measureName, span.markStart, markEnd);
      } finally {
        performance.clearMarks(span.markStart);
        performance.clearMarks(markEnd);
        performance.clearMeasures(span.measureName);
      }
    }
    this.record(span.name, durationMs);
  }

  public record(name: string, durationMs: number): void {
    if (!this.enabled || !Number.isFinite(durationMs)) {
      return;
    }

    let data: ViewerProfilerPhaseData | undefined = this.phaseData.get(name);
    if (data === undefined) {
      if (this.phaseData.size >= MAX_PHASES) {
        return;
      }
      data = {
        count: 0,
        totalMs: 0,
        maxMs: 0,
        lastMs: 0,
        slowCount: 0,
        samples: [],
      };
      this.phaseData.set(name, data);
    }
    data.count++;
    data.totalMs += durationMs;
    data.maxMs = Math.max(data.maxMs, durationMs);
    data.lastMs = durationMs;
    if (name === 'frame.update' && durationMs >= this.slowFrameMs) {
      data.slowCount++;
    }
    data.samples.push(durationMs);
    if (data.samples.length > MAX_SAMPLES_PER_PHASE) {
      data.samples.shift();
    }
  }

  public recordCounter(name: string, value: number): void {
    if (!this.enabled || !Number.isFinite(value)) {
      return;
    }

    let data: ViewerProfilerCounterData | undefined = this.counterData.get(name);
    if (data === undefined) {
      if (this.counterData.size >= MAX_COUNTERS) {
        return;
      }
      data = {
        count: 0,
        total: 0,
        min: value,
        max: value,
        last: value,
      };
      this.counterData.set(name, data);
    }
    data.count++;
    data.total += value;
    data.min = Math.min(data.min, value);
    data.max = Math.max(data.max, value);
    data.last = value;
  }

  public startGpuSpan(gl: WebGL2RenderingContext, name: string): HsViewerGpuSpan | null {
    if (!this.enabled || !this.deepGpu) {
      return null;
    }
    this.initializeGpuTimer(gl);
    if (this.gpuExtension === null) {
      return null;
    }
    const query: WebGLQuery | null = gl.createQuery();
    if (query === null) {
      this.gpuTimerMessage = 'WebGL query allocation failed.';
      return null;
    }
    try {
      gl.beginQuery(this.gpuExtension.TIME_ELAPSED_EXT, query);
      return { gl, extension: this.gpuExtension, query, name };
    } catch (error: unknown) {
      gl.deleteQuery(query);
      this.gpuTimerMessage = error instanceof Error ? error.message : 'WebGL GPU query could not start.';
      return null;
    }
  }

  public endGpuSpan(span: HsViewerGpuSpan | null): void {
    if (span === null) {
      return;
    }
    try {
      span.gl.endQuery(span.extension.TIME_ELAPSED_EXT);
      this.pendingGpuQueries.push(span);
    } catch (error: unknown) {
      span.gl.deleteQuery(span.query);
      this.gpuTimerMessage = error instanceof Error ? error.message : 'WebGL GPU query could not finish.';
    }
  }

  public pollGpuQueries(): void {
    if (!this.enabled || !this.deepGpu || this.pendingGpuQueries.length === 0) {
      return;
    }
    for (let index: number = this.pendingGpuQueries.length - 1; index >= 0; index--) {
      const pending: ViewerPendingGpuQuery = this.pendingGpuQueries[index];
      const available: boolean = Boolean(
        pending.gl.getQueryParameter(pending.query, pending.gl.QUERY_RESULT_AVAILABLE)
      );
      if (!available) {
        continue;
      }
      const disjoint: boolean = Boolean(pending.gl.getParameter(pending.extension.GPU_DISJOINT_EXT));
      if (!disjoint) {
        const elapsedNanoseconds: number = Number(pending.gl.getQueryParameter(pending.query, pending.gl.QUERY_RESULT));
        this.record(`${pending.name}.gpu`, elapsedNanoseconds / 1_000_000);
      }
      pending.gl.deleteQuery(pending.query);
      this.pendingGpuQueries.splice(index, 1);
    }
  }

  public snapshot(): HsViewerProfilerSnapshot {
    const phases: HsViewerProfilerPhaseSnapshot[] = Array.from(this.phaseData.entries())
      .map(([name, data]: [string, ViewerProfilerPhaseData]) => {
        const samples: number[] = [...data.samples].sort((a: number, b: number) => a - b);
        return {
          name,
          count: data.count,
          totalMs: data.totalMs,
          averageMs: data.totalMs / Math.max(1, data.count),
          p50Ms: this.percentile(samples, 0.5),
          p95Ms: this.percentile(samples, 0.95),
          maxMs: data.maxMs,
          lastMs: data.lastMs,
          slowCount: data.slowCount,
        };
      })
      .sort((a: HsViewerProfilerPhaseSnapshot, b: HsViewerProfilerPhaseSnapshot) => b.p95Ms - a.p95Ms);
    const counters: HsViewerProfilerCounterSnapshot[] = Array.from(this.counterData.entries())
      .map(([name, data]: [string, ViewerProfilerCounterData]) => ({
        name,
        count: data.count,
        total: data.total,
        average: data.total / Math.max(1, data.count),
        min: data.min,
        max: data.max,
        last: data.last,
      }))
      .sort((a: HsViewerProfilerCounterSnapshot, b: HsViewerProfilerCounterSnapshot) => b.last - a.last);
    return {
      mode: this.enabled ? (this.deepGpu ? 'deep' : 'cpu') : 'off',
      phaseCount: phases.length,
      frameCount: this.phaseData.get('frame.update')?.count ?? 0,
      slowFrameCount: this.phaseData.get('frame.update')?.slowCount ?? 0,
      longTaskCount: this.longTaskCount,
      longTaskTotalMs: this.longTaskTotalMs,
      longTaskMaxMs: this.longTaskMaxMs,
      userTimingSupported: typeof performance !== 'undefined' && typeof performance.mark === 'function',
      longTaskSupported: this.longTaskObserver !== null,
      gpuTimerSupported: this.gpuExtension !== null,
      gpuTimerMessage: this.gpuTimerMessage,
      phases: phases.slice(0, 12),
      counters: counters.slice(0, 16),
      events: [...this.events],
    };
  }

  private percentile(sortedSamples: number[], percentile: number): number {
    if (sortedSamples.length === 0) {
      return 0;
    }
    const index: number = Math.min(sortedSamples.length - 1, Math.floor((sortedSamples.length - 1) * percentile));
    return sortedSamples[index];
  }

  private startLongTaskObserver(): void {
    if (this.longTaskObserver !== null || typeof PerformanceObserver === 'undefined') {
      return;
    }
    try {
      const observer: PerformanceObserver = new PerformanceObserver((list: PerformanceObserverEntryList) => {
        for (const entry of list.getEntries()) {
          this.longTaskCount++;
          this.longTaskTotalMs += entry.duration;
          this.longTaskMaxMs = Math.max(this.longTaskMaxMs, entry.duration);
        }
      });
      observer.observe({ entryTypes: ['longtask'] });
      this.longTaskObserver = observer;
    } catch {
      this.longTaskObserver = null;
    }
  }

  private stopLongTaskObserver(): void {
    this.longTaskObserver?.disconnect();
    this.longTaskObserver = null;
  }

  private initializeGpuTimer(gl: WebGL2RenderingContext): void {
    if (this.gpuExtension !== null) {
      return;
    }
    const extension: ViewerGpuTimerExtension | null = gl.getExtension(
      'EXT_disjoint_timer_query_webgl2'
    ) as ViewerGpuTimerExtension | null;
    this.gpuExtension = extension;
    this.gpuTimerMessage =
      extension === null
        ? 'EXT_disjoint_timer_query_webgl2 is unavailable in this browser or GPU driver.'
        : 'GPU timer queries are available.';
  }

  private releaseGpuQueries(): void {
    for (const pending of this.pendingGpuQueries) {
      pending.gl.deleteQuery(pending.query);
    }
    this.pendingGpuQueries.length = 0;
  }
}
